import { Label } from '@/components/base/label'
import { Icon } from '@/components/common/icons/Icon'
import { SliderWithTicks } from '@/components/SliderWithTicks'
import { DEBOUNCE_SLIDER_TIMEOUT_MS } from '@/helpers/constants'
import { validateMinMax } from '@/helpers/utils'
import { useOptimisticUpdateWithPromise } from '@/hooks/useOptimisticUpdateWithPromise'
import { useRoomStoreActions } from '@/stores/room'
import { useCallback, useId } from 'react'

// Range of microphone volume set from the SDK/server
const MIC_VOLUME_RANGE = {
  MAX: 50,
  MIN: -50,
} as const

interface SliderSetMicrophoneProps {
  disabled: boolean
  memberId: string
  microphoneVolume: number
}

export const SliderSetMicrophone = ({
  disabled,
  memberId,
  microphoneVolume,
}: SliderSetMicrophoneProps) => {
  const micVolumeId = useId()

  const { setMicrophoneVolume } = useRoomStoreActions()

  const handleUpdateCallback = useCallback(
    (value: number) => {
      if (validateMinMax(value, MIC_VOLUME_RANGE.MIN, MIC_VOLUME_RANGE.MAX)) {
        return setMicrophoneVolume({ memberId, value })
      } else {
        throw new Error('Invalid microphone volume value')
      }
    },
    [memberId, setMicrophoneVolume],
  )

  const { optimisticValue, handleUpdate } = useOptimisticUpdateWithPromise(
    microphoneVolume,
    handleUpdateCallback,
    DEBOUNCE_SLIDER_TIMEOUT_MS,
  )

  const handleValueChange = (value: number[]) => {
    const valueToUpdate = value.at(0)
    if (valueToUpdate !== undefined) {
      handleUpdate(valueToUpdate)
    }
  }

  return (
    <>
      <div className="flex items-center gap-x-3 py-4 pr-2">
        <Icon size="xl" tag="mic" />
        <Label htmlFor={micVolumeId}>Mic Volume</Label>
      </div>
      <SliderWithTicks
        disabled={disabled}
        id={micVolumeId}
        max={MIC_VOLUME_RANGE.MAX}
        min={MIC_VOLUME_RANGE.MIN}
        onValueChange={handleValueChange}
        step={1}
        stops={9}
        value={[optimisticValue]}
      />
    </>
  )
}
